import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { waResetLink, waInviteLink } from "@/lib/whatsapp";
import { RoleRequestsSection, RegistrationsSection } from "@/pages/user_rows";
import { EditUserModal, ApproveRegistrationModal } from "@/pages/user_modals";
import { ActiveMembersSection } from "@/pages/users_active";

const HIDDEN_IN_LIST = ["pending_email", "pending_approval", "rejected"];

// Gestion des utilisateurs : inscriptions à valider + membres actifs (voir user_rows / user_modals).
export default function Users() {
  const { t } = useApp();
  const { user, isRole } = useAuth();
  const isPresident = isRole("president");
  const [users, setUsers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [editing, setEditing] = useState(null);
  const [approving, setApproving] = useState(null);

  const load = useCallback(async () => {
    if (isPresident) {
      const usRes = await api.get("/auth/users");
      const regRes = await api.get("/auth/registrations");
      setUsers(usRes.data);
      setRegistrations(regRes.data);
    }
    try {
      const { data } = await api.get("/auth/role-requests");
      setRequests(data);
    } catch (e) {
      // 403 attendu pour les rôles sans accès aux demandes de rôle — silencieux.
      if (e?.response?.status !== 403) console.warn("users: échec chargement demandes de rôle", e?.message);
    }
  }, [isPresident]);

  useEffect(() => { load(); }, [load]);

  const resetPin = async (u) => {
    if (!window.confirm(t("reset_pin_confirm"))) return;
    try {
      const { data } = await api.post(`/auth/users/${u.id}/reset-pin`);
      toast.success(t("pin_reset_done"));
      load();
      window.open(waResetLink(data), "_blank");
    } catch (e) {
      toast.error(t("login_error"));
    }
  };

  const regenerate = async (u) => {
    try {
      const { data } = await api.post(`/auth/users/${u.id}/regenerate-invite`);
      toast.success(t("code_regenerated"));
      load();
      window.open(waInviteLink(data), "_blank");
    } catch (e) {
      toast.error(t("login_error"));
    }
  };

  const toggleActive = async (u) => {
    const action = u.status === "disabled" ? "enable" : "disable";
    try {
      await api.post(`/auth/users/${u.id}/${action}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("login_error"));
    }
  };

  const approveReq = async (rid, ok) => {
    try {
      await api.put(`/auth/role-requests/${rid}/${ok ? "approve" : "reject"}`);
      toast.success(ok ? t("role_approved_toast") : t("role_rejected_toast"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("login_error"));
    }
  };

  const rejectRegistration = async (r) => {
    if (!window.confirm(t("reject_registration_confirm"))) return;
    try {
      await api.post(`/auth/registrations/${r.id}/reject`);
      toast.success(t("registration_rejected_toast"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("login_error"));
    }
  };

  const openWhatsApp = (u, kind) => window.open(kind === "reset" ? waResetLink(u) : waInviteLink(u), "_blank");
  const closeEdit = () => setEditing(null);
  const doneEdit = () => { setEditing(null); load(); };
  const closeApprove = () => setApproving(null);
  const doneApprove = () => { setApproving(null); load(); };

  const activeUsers = users.filter((u) => !HIDDEN_IN_LIST.includes(u.status));

  return (
    <div className="space-y-5" data-testid="users-page">
      <h1 className="text-2xl font-black">{t("user_management")}</h1>
      {requests.length > 0 && (
        <RoleRequestsSection requests={requests} currentUserId={user.id} onDecide={approveReq} t={t} />
      )}
      {isPresident && (
        <RegistrationsSection registrations={registrations} onApprove={setApproving} onReject={rejectRegistration} t={t} />
      )}
      {isPresident && (
        <ActiveMembersSection users={activeUsers} currentUserId={user.id} t={t} onWhatsApp={openWhatsApp}
          onRegen={regenerate} onEdit={setEditing} onResetPin={resetPin} onToggle={toggleActive} />
      )}
      {editing && <EditUserModal target={editing} onClose={closeEdit} onDone={doneEdit} t={t} />}
      {approving && <ApproveRegistrationModal target={approving} onClose={closeApprove} onDone={doneApprove} t={t} />}
    </div>
  );
}
